<template>
  <div class="nye-page-container">
    <!-- 1. Hero Section -->
    <header class="hero-section" :style="{ backgroundImage: `url(${mainImage})` }">
      <div class="hero-overlay">
        <h1 class="hero-headline">TULUM  NYE FESTIVAL <br>2025-2026</h1>
        <p class="hero-subheadline">Dance To Celebrate</p>
      </div>
    </header>

    <div class="page-content">
      <div class="row">
        <!-- Main Content Column -->
        <section class="col-lg-8 col-sm-12">
          <!-- Intro Section -->
          <section class="intro-section">
            <p>
              Starting from <strong>late December to March</strong>, Tulum transforms into the global epicenter for electronic music. The world's best <router-link :to="{path: 'tulum-dj-schedule'}">DJs and producers</router-link> descend upon magnificent jungle and beach locations with one shared goal: to celebrate life through dance.
            </p>
          </section>

          <!-- Added Visual Break -->
          <ResponsiveImage :image="visualBreakImage" alt="Vibrant party scene in Tulum" class="visual-break"/>

          <!-- Highlight Card Section -->
          <section class="highlight-card-section">
            <h2>Tulum During The Years</h2>
            <HighLightCard />
            <!-- ✅ NEW: Added descriptive text -->
            <p class="highlight-outro-text">
              ...epic performances to live and remember. Let's build new memories this 2026!
            </p>
          </section>

          <!-- Main Event Line Section -->
          <section class="event-line-section">
            <div class="event-line-header">
              <h2>High Season Event Line</h2>
              <div class="search-box">
                <input
                  type="text"
                  v-model="searchTerm"
                  placeholder="Search by event, artist, or venue..."
                />
              </div>
            </div>

            <!-- Grouped Event List -->
            <div class="event-list-container">
              <div v-for="(eventsOnDay, date) in filteredAndGroupedEvents" :key="date">
                <h3 class="sticky-date-header">
                  <!-- ✅ NEW: Calendar SVG Icon -->
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-calendar"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
                  <span>{{ formatDateHeader(date) }}</span>
                </h3>
                <div class="day-events">
                  <EventCard
                    v-for="(event, idx) in eventsOnDay"
                    :key="event.slug"
                    :event="event"
                    :inverted="idx % 2 !== 0"
                  />
                </div>
              </div>
              <div v-if="Object.keys(filteredAndGroupedEvents).length === 0 && !this.$store.state.status.loading" class="no-results">
                <p>No events match your search. Try another keyword!</p>
              </div>
            </div>
          </section>

          <!-- ✅ NEW: Top Venues & Brands Section -->
          <section class="top-venues-section">
            <h2>Iconic Venues & Brands of the High Season</h2>
            <div class="venues-grid">
              <div v-for="item in topVenues" :key="item.name" class="venue-card">
                <router-link :to="item.link">
                  <ResponsiveImage :image="item.image"  :alt="item.name" class="venue-image"/>
                  <div class="venue-card-overlay">
                    <h3>{{ item.name }}</h3>
                  </div>
                </router-link>
              </div>
            </div>
          </section>

        </section>

        <!-- Sidebar Column -->
        <aside class="col-lg-4 col-sm-12">
          <div class="sidebar-sticky">
            <!-- Reservations Card -->
            <section class="cta-section card">
              <img v-lazy="astroImg + '?h=100'" height="100px" width="100px" alt="Info & Support"/>
              <h3>Need Help Planning Your NYE Experience?</h3>
              <p>Your single source for all events & clubs needs. From tickets to VIP tables, our concierge team is here to help you craft the perfect high-season adventure.</p>
              <RsvpViaDialog
                text="INFO & RSVP"
                legend="<p>NYE 2026 Information & Reservations</p>"
              />
            </section>

            <!-- SmartMenu with other content -->
            <SmartMenu :links="links" :logo="astroImg" top-section-name="TALK TO US" cta-text="DROP US A LINE">
              <p>All Clubs & Events <br />Information & Reservations</p>
            </SmartMenu>

            <!-- Featured Brands Section -->
            <section class="featured-brands-section card">
              <h2>Featuring World-Class Production Brands</h2>
              <BrandCollection />
            </section>
          </div>
        </aside>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { eventDateMixin } from '@/common/eventDateMixin';
import EventCard from '../../components/EventCard/EventCard';
import HeadImage from "@/components/HeadImage/HeadImage";
import BrandCollection from "@/components/Cards/BrandCollection";
import SmartMenu from "@/components/SmartMenu/SmartMenu";
import RsvpViaDialog from "@/components/Reservations/RsvpViaDialog";
import HighLightCard from "@/components/Cards/HighLightCard";
import ResponsiveImage from '@/components/HeadImage/ResponsiveImage';

export default {
  name: 'TulumNYE',
  mixins: [eventDateMixin],
  props: ['slug'],
  components: {
    RsvpViaDialog,
    SmartMenu,
    EventCard,
    HeadImage,
    BrandCollection,
    HighLightCard,
    ResponsiveImage,
  },
  metaInfo() {
    return {
      title: 'Tulum NYE 2025/2026 · New Year Festival · Event Line',
      link: [{ rel: 'canonical', href: 'https://www.tulum.party/tulum-new-year-festival-2025' }],
      meta: [
        { name: 'description', content: `The official event line for Tulum's NYE 2025-2026 high season. Find tickets, tables, and information for all major festivals and parties.` },
        { name: 'keywords', content: `Tulum NYE 2026, Tulum New Year Festival, Zamna 2026, Afterlife Tulum 2026, Tulum Rave, DJ Parties` },
        { property: 'og:title', content: `Tulum NYE 2025-2026. Tables, Tickets & Information` },
        { property: 'og:url', content: `https://www.tulum.party/tulum-new-year-festival-2025` },
        { property: 'og:image', content: 'https://imgix.cosmicjs.com/1ff4b960-d82b-11ef-bee4-3bb1d3c55332-zamna-tulum-2025-extra-9.jpg' },
      ],
    };
  },
  data() {
    return {
      searchTerm: '',
      mainImage: 'https://imgix.cosmicjs.com/1ff4b960-d82b-11ef-bee4-3bb1d3c55332-zamna-tulum-2025-extra-9.jpg',
      astroImg: 'https://imgix.cosmicjs.com/6ed6b0f0-60cd-11ee-b3cd-9debaa756aee-image.png',
      visualBreakImage: 'https://imgix.cosmicjs.com/f0e84200-332e-11ef-a504-63e081e4680f-brand-day-zero-8.png',
      topVenues: [
        { name: 'Zamna', image: 'https://imgix.cosmicjs.com/e84ef750-d82a-11ef-bee4-3bb1d3c55332-zamna-tulum-2025-3.jpg', link: '/brands/zamna-tulum'},
        { name: 'Tehmplo', image: 'https://imgix.cosmicjs.com/0fe2bdc0-cc2c-11ef-8a63-eb57d6c77a36-tehmplo-main.jpg', link: '/brands/tehmplo-tulum'},
        { name: 'Vagalume', image: 'https://imgix.cosmicjs.com/323fb9e0-d4cf-11ee-9ce5-59949019255e-vagalume-7.jpg', link: '/tulum-clubs/vagalume-tulum'},
        { name: 'Papaya Playa Project', image: 'https://imgix.cosmicjs.com/827cb160-b345-11ef-bee4-3bb1d3c55332-club-papaya-playa-foto.jpg', link: '/tulum-clubs/papaya-playa'},
        { name: 'Bonbonniere', image: 'https://imgix.cosmicjs.com/491a08e0-b45b-11ed-bce9-6ddb530a836d-Bonbonniere-Tulum.jpg', link: '/tulum-clubs/bonbonniere-tulum'},
        { name: 'SET Underground', image: 'https://imgix.cosmicjs.com/bd804e90-b044-11ec-97bc-19d12908cbbe-set-underground-cover.jpg', link: '/brands/set-underground-tulum'},
      ]
    };
  },
  computed: {
    ...mapState({
      festival: state => state.festival,
      links: state => state.links || [],
    }),
    allFestivalParties() {
      if (!this.festival || !this.festival.metadata || !this.festival.metadata.parties) {
        return [];
      }
      return [...this.festival.metadata.parties].sort((a, b) => {
        const dateA = new Date(`${a.metadata.date}T${a.metadata.start_time || '00:00'}`);
        const dateB = new Date(`${b.metadata.date}T${b.metadata.start_time || '00:00'}`);
        return dateA.getTime() - dateB.getTime();
      });
    },
    featuredParties() {
      return this.allFestivalParties.slice(0, 3);
    },
    filteredAndGroupedEvents() {
      let events = this.allFestivalParties;

      if (this.searchTerm) {
        const query = this.searchTerm.toLowerCase();
        events = events.filter(item =>
          item.slug.toLowerCase().includes(query) ||
          item.title.toLowerCase().includes(query) ||
          (item.metadata.lineup || '').toLowerCase().includes(query)
        );
      }

      return events.reduce((acc, event) => {
        const date = event.metadata.date;
        if (!acc[date]) acc[date] = [];
        acc[date].push(event);
        return acc;
      }, {});
    },
  },
  created() {
    this.$store.dispatch('getFestival', { slug: this.slug || 'tulum-new-year-festival-2025' });
    this.$store.dispatch('getLinks', { slug: 'tulum-new-year-festival' });
  },
  methods: {
    formatDateHeader(dateString) {
      const date = new Date(`${dateString}T12:00:00`);
      return new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'long', day: 'numeric' }).format(date).toUpperCase();
    }
  }
};
</script>

<style scoped>
.nye-page-container {
  background-color: #1a1a1c;
  color: #f0f0f0;
}
.hero-section {
  height: 70vh;
  min-height: 500px;
  background-size: cover;
  background-position: center;
  position: relative;
  border-radius: 12px;
  overflow: hidden;
}
.hero-overlay {
  background: linear-gradient(to top, rgba(26, 26, 28, 1) 10%, rgba(26, 26, 28, 0.3) 100%);
  position: absolute;
  top: 0; left: 0; width: 100%; height: 100%;
  display: flex; flex-direction: column; justify-content: center; align-items: center;
  text-align: center; padding: 2rem;
}
.hero-headline {
  font-family: 'Oswald', sans-serif;
  font-size: 4rem;
  font-weight: 900;
  color: #fff;
  text-shadow: 2px 2px 10px rgba(0, 0, 0, 0.8);
  max-width: 900px;
  line-height: 1.1;
  margin: 0;
}
.hero-subheadline { font-size: 1.25rem; color: #88adb4 !important; margin-top: 1rem; background-color: #1a1818;width: 100%}
.page-content { max-width: 1200px; margin: 0 auto; padding: 3rem 1.5rem; }
section { margin-bottom: 4rem; }
section h2 { text-align: center; font-size: 2.5rem; font-weight: 700; color: #d4af37; margin-bottom: 2rem; }

.intro-section p { font-size: 1.2rem; line-height: 1.8; text-align: center; max-width: 800px; margin: 0 auto; color: #88adb4 !important; }

.visual-break {
  margin: 3rem 0;
  border-radius: 12px;
  overflow: hidden;
}

.highlight-outro-text {
  text-align: center;
  font-style: italic;
  color: #aaa;
  margin-top: 1rem;
}

.featured-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
}

.event-line-section {
  padding: 2rem;
  border-radius: 12px;
  background: linear-gradient(135deg, #2b2b2d, #1f1f21);
}

.event-line-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 2rem;
}
.event-line-header h2 { margin: 0; text-align: left; }
.search-box input {
  padding: 0.8rem;
  background-color: #3a3a3c;
  border: 1px solid #555;
  border-radius: 8px;
  color: #f0f0f0;
  font-size: 1rem;
  min-width: 300px;
}

.sticky-date-header {
  position: sticky;
  top: 0;
  background-color: #2a2a2d;
  padding: 0.75rem 1.5rem;
  z-index: 10;
  font-size: 1.2rem;
  font-weight: 700;
  border-bottom: 2px solid #d4af37;
  text-transform: uppercase;
  color: #fff;
  margin-top: 2rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.day-events {
  padding: 1.5rem 0;
}
.no-results { text-align: center; padding: 40px; font-size: 1.2em; color: #888; }

.cta-section.card, .featured-brands-section.card {
  background: linear-gradient(135deg, #2b2b2d, #252528);
  text-align: center;
  padding: 2rem;
  margin-top: 2rem;
  border: 1px solid #444;
}
.cta-section h3 { font-size: 2rem; color: #fff; margin-top: 1rem; }
.cta-section p { max-width: 600px; margin: 1rem auto 1.5rem auto; color: #ccc; }

.sidebar-sticky {
  position: sticky;
  top: 20px;
}

.top-venues-section .venues-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
}
.venue-card {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  height: 350px;
}
.venue-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease;
}
.venue-card:hover .venue-image {
  transform: scale(1.05);
}
.venue-card-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 1rem;
  background: linear-gradient(to top, rgba(0,0,0,0.9), transparent);
  text-align: center;
}
.venue-card-overlay h3 {
  color: #fff;
  font-size: 1.5rem;
  font-weight: bold;
  margin: 0;
  text-shadow: 1px 1px 5px #000;
}


@media (max-width: 992px) {
  .sidebar-sticky {
    position: static;
  }
}
</style>
